import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { getOwner } from '@ember/application';
import * as Sentry from '@sentry/ember';
import getConfig from '../lib/get-config.js';
import { supportsCssLayers, installCssLayerFallback } from '../lib/css-layer-fallback.js';
import { uninstallLayerHistory, installLayerHistory } from '../lib/nv-layer-history.js';
import { stackedTokens } from '../lib/nv-layer-stack.js';
import { uninstallKeyboardInset, installKeyboardInset } from '../lib/keyboard-inset.js';
import { uninstallScrollRestore, installScrollRestore } from '../lib/nv-scroll-restore.js';
import { installFeedbackTrail, uninstallFeedbackTrail } from '../lib/nv-feedback-trail.js';
import { isRegisteredPristine } from '../lib/pristine-registry.js';

var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
const UI_STORAGE_KEY = 'beebee_ui';
const UI_CLASSICA = 'classica';
const UI_NUOVA = 'nuova';
const UI_VERSIONS = [UI_CLASSICA, UI_NUOVA];
// Production preview: the tenants (organization slug) that see the switch before the release.
const UI_PREVIEW_ORGS = ['fisiotest', 'poppixsrl'];
// Set on a device by `?ui=new`: the preview is unlocked there for ONE tenant (owner, 2026-10-14: switching
// organization on the same device must not carry the new UI along). Value = the tenant slug, or
// UI_UNLOCK_PENDING when the link was opened before login (bound to the first tenant that logs in; the legacy
// value '1' counts as pending).
const UI_UNLOCK_KEY = 'beebee_ui_unlock';
const UI_UNLOCK_PENDING = '*';
function readUnlockValue() {
  try {
    return window.localStorage?.getItem(UI_UNLOCK_KEY) ?? '';
  } catch {
    return '';
  }
}
function writeUnlockValue(value) {
  try {
    if (value) {
      window.localStorage?.setItem(UI_UNLOCK_KEY, value);
    } else {
      window.localStorage?.removeItem(UI_UNLOCK_KEY);
    }
  } catch {
    // storage blocked: the unlock still holds for this page load
  }
}
function isPendingUnlock(value) {
  return value === UI_UNLOCK_PENDING || value === '1';
}

// `?ui=new` / `?ui=old` (also nuova / classica / classic); anything else is ignored.
function readQueryVersion() {
  try {
    const value = new URLSearchParams(window.location.search).get('ui');
    if (value === 'new' || value === UI_NUOVA) return UI_NUOVA;
    if (value === 'old' || value === 'classic' || value === UI_CLASSICA) return UI_CLASSICA;
  } catch {
    // no location (tests, SSR): no query switch
  }
  return null;
}
function readStoredVersion() {
  // Reading storage can itself throw (blocked site data, a document that is
  // already unloading): the switch must still boot, on the classic default.
  try {
    const stored = window.localStorage?.getItem(UI_STORAGE_KEY);
    return UI_VERSIONS.includes(stored) ? stored : UI_CLASSICA;
  } catch {
    return UI_CLASSICA;
  }
}
function tagSentry(version) {
  // Every error report says which UI the user was on. Sentry.setTag is a
  // no-op when the SDK was never initialised (tests, dev); the guard only
  // covers a build where the import resolved to something unexpected.
  try {
    if (typeof Sentry?.setTag === 'function') {
      Sentry.setTag('ui', version);
    }
  } catch {
    // reporting is best effort, never a reason to break the switch
  }
}
let UiService = (_class = class UiService extends Service {
  constructor() {
    super(...arguments);
    _initializerDefineProperty(this, "version", _descriptor, this);
    // Per-app opt-in, flipped by the app's own instance-initializer (medical
    // first). See the MAP gotcha: without it the stored choice is ignored.
    _initializerDefineProperty(this, "enabled", _descriptor2, this);
    // `?ui=new` was used on this device for the current tenant (see the MAP gotcha «PREVIEW GATE»).
    _initializerDefineProperty(this, "unlocked", _descriptor3, this);
    // The raw unlock value (tenant slug or pending) as read from / written to storage.
    this.unlockValue = '';
    // Apps enabled with enable({ queryOnly: true }) (the end-customer apps: beebeeboard-booking, e-commerce): no switch
    // anywhere; the new UI applies only on a device unlocked with `?ui=new` (or in the test build).
    _initializerDefineProperty(this, "queryOnly", _descriptor4, this);
    // Tenant slug pushed by session.setMe() (me.organization.organization). Never looked up from here: an eager
    // service:session lookup at enable() instantiated the real session before the test mocks and its HTTPS
    // bootstrap redirect killed the test runner page.
    _initializerDefineProperty(this, "tenant", _descriptor5, this);
    // «UI nuova» (MOBILE-NATIVE-PLAN W1.2): reload / tab close with an open edit modal holding changes, or a save in
    // flight, asks first (the browser's own prompt). Route changes need no guard here: Back is owned by the open
    // overlay (lib/nv-layer-history), and the modal's own ✕ / Escape / Back path shows «Modifiche non salvate».
    this._onBeforeUnload = null;
    this.version = readStoredVersion();
    this.unlockValue = readUnlockValue();
    this._syncUnlocked();
  }

  // Bind a pending unlock to the tenant now logged in, then decide whether this tenant is the unlocked one.
  _syncUnlocked() {
    if (this.tenant && isPendingUnlock(this.unlockValue)) {
      this.unlockValue = this.tenant;
      writeUnlockValue(this.unlockValue);
    }
    const value = this.unlockValue;
    this.unlocked = Boolean(value) && (value === this.tenant || isPendingUnlock(value));
  }
  get environment() {
    try {
      return getConfig(this)?.environment ?? 'production';
    } catch {
      return 'production';
    }
  }
  /** Called by session.setMe() on login / logout; re-applies the gate. */
  setTenant(slug) {
    this.tenant = String(slug ?? '').toLowerCase();
    this._syncUnlocked();
    this.apply();
  }

  /** A preview (test) tenant: the switch is always offered there (in development as well: owner, 2026-10-14). */
  get previewSwitch() {
    return this.enabled && !this.queryOnly &&
    // the new UI needs cascade layers (classic is isolated in `@layer classica`): never offered without them
    supportsCssLayers() && UI_PREVIEW_ORGS.includes(this.tenant);
  }

  /**
   * The switch is shown to EVERY user of an app that opted in with a switch (owner, 2026-10-22: the new UI is
   * open to all, both ways): clinic, segreteria, guide, bike, … Query-only apps (the public booking and the
   * e-commerce shop, used by end customers) never show it. Needs cascade layers (classic lives in a layer).
   */
  get canChoose() {
    return this.enabled && !this.queryOnly && supportsCssLayers();
  }

  /**
   * The stored 'nuova' may apply: any app with the switch (owner, 2026-10-22); a query-only app only on a device
   * unlocked with `?ui=new` for this tenant, or in the test build.
   */
  get canUse() {
    if (!this.enabled || !supportsCssLayers()) return false;
    return !this.queryOnly || this.environment === 'test' || this.unlocked;
  }
  get isNuova() {
    return this.canUse && this.version === UI_NUOVA;
  }

  /**
   * Opt this app in: the stored choice (or a `?ui=` in the URL) now applies. Idempotent.
   * @param {{ queryOnly?: boolean }} [options] queryOnly: no switch, only `?ui=new` unlocks the new UI
   */
  enable({
    queryOnly = false
  } = {}) {
    this.enabled = true;
    this.queryOnly = Boolean(queryOnly);
    // browsers without cascade layers drop the whole layered classic stylesheet: re-serve it unwrapped
    installCssLayerFallback();
    const fromQuery = readQueryVersion();
    if (fromQuery) {
      this.unlockValue = fromQuery === UI_NUOVA ? this.tenant || UI_UNLOCK_PENDING : '';
      writeUnlockValue(this.unlockValue);
      this._syncUnlocked();
      this.setVersion(fromQuery);
      return;
    }
    this.apply();
  }

  /**
   * @param {string} value 'classica' | 'nuova'
   * @returns {boolean} false (and no change) for an unknown value
   */
  setVersion(value) {
    if (!UI_VERSIONS.includes(value)) {
      return false;
    }
    this.version = value;
    try {
      window.localStorage?.setItem(UI_STORAGE_KEY, value);
    } catch {
      // storage full/blocked: the choice still applies for this session
    }
    this.apply();
    return true;
  }
  willDestroy() {
    super.willDestroy(...arguments);
    // the Back handler holds this owner's router: never outlive the app instance (tests boot many)
    uninstallLayerHistory();
    uninstallScrollRestore();
    this.uninstallLeaveGuard();
    uninstallKeyboardInset();
  }
  installLeaveGuard() {
    if (this._onBeforeUnload || typeof window === 'undefined') return;
    this._onBeforeUnload = event => {
      if (!this.isNuova || !this.hasOpenUnsavedWork()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', this._onBeforeUnload);
  }
  uninstallLeaveGuard() {
    if (!this._onBeforeUnload) return;
    window.removeEventListener('beforeunload', this._onBeforeUnload);
    this._onBeforeUnload = null;
  }

  /**
   * True when leaving now loses work: an open modal / sheet whose record has changes (a NEW record untouched since
   * its form opened — pristine registry, as BbModal closeModal — does not count), or a save in flight. Unlike
   * hasUnsavedChanges it ignores the route models: a page record dirtied by open-time recomputation is not the
   * user's work and must not prompt on every reload.
   * @returns {boolean}
   */
  hasOpenUnsavedWork() {
    const dirtyOpen = stackedTokens().some(token => {
      const item = token?.args?.item;
      if (!item?.hasDirtyAttributes) return false;
      return !(item.isNew && isRegisteredPristine(item));
    });
    if (dirtyOpen) return true;
    const applicationController = getOwner(this)?.lookup('controller:application');
    return (applicationController?._savingCount ?? 0) > 0;
  }
  apply() {
    const html = document.documentElement;
    if (this.isNuova) {
      html.dataset.ui = UI_NUOVA;
      installLayerHistory(getOwner(this)?.lookup('service:router'));
      installScrollRestore(getOwner(this)?.lookup('service:router'));
      installFeedbackTrail(getOwner(this)?.lookup('service:router'));
      this.installLeaveGuard();
      installKeyboardInset();
    } else {
      delete html.dataset.ui;
      uninstallLayerHistory();
      uninstallScrollRestore();
      uninstallFeedbackTrail();
      this.uninstallLeaveGuard();
      uninstallKeyboardInset();
    }
    if (this.enabled) {
      tagSentry(this.isNuova ? UI_NUOVA : UI_CLASSICA);
    }
  }

  /**
   * Dirty-form guard for the switch. Changing UI re-mounts the forked
   * templates: dirty attributes survive (they live on the record), but
   * component-local state is dropped and ember-concurrency cancels any task
   * of the unmounted component at its next yield. There is no app-wide
   * leave-guard in the shell, so this reuses the same predicate the modal
   * shell uses before closing (`item.hasDirtyAttributes`, bb-modal.js
   * closeModal) on every record the shell is showing:
   *   - every edit modal on the modal stack,
   *   - the model of each route in the current route chain (view pages),
   * plus a save still in flight on the application controller.
   * @returns {boolean}
   */
  hasUnsavedChanges() {
    const owner = getOwner(this);
    if (!owner) {
      return false;
    }
    const stack = owner.lookup('service:modal-stack')?.modalComponents ?? [];
    if (stack.some(descriptor => descriptor?.item?.hasDirtyAttributes)) {
      return true;
    }

    // `_savingCount` is the counter behind _startSaving/_stopSaving (the
    // `beebee-saving` body class) — the only shell-wide in-flight-save signal.
    const applicationController = owner.lookup('controller:application');
    if ((applicationController?._savingCount ?? 0) > 0) {
      return true;
    }
    let routeInfo = owner.lookup('service:router')?.currentRoute ?? null;
    while (routeInfo) {
      if (routeInfo.attributes?.hasDirtyAttributes) {
        return true;
      }
      routeInfo = routeInfo.parent;
    }
    return false;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "version", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return UI_CLASSICA;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "enabled", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "unlocked", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "queryOnly", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "tenant", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _class);

export { UI_CLASSICA, UI_NUOVA, UI_PREVIEW_ORGS, UI_STORAGE_KEY, UI_UNLOCK_KEY, UI_UNLOCK_PENDING, UI_VERSIONS, UiService as default };
//# sourceMappingURL=ui.js.map
